import React, { useEffect, useMemo, useRef, useState } from "react";
import "./paginaNivel.css";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faBolt,
  faChevronRight,
  faDroplet,
  faMedal,
  faTrophy,
  faUtensils,
} from "@fortawesome/free-solid-svg-icons";

import { getNivelTier } from "../../utils/nivelTiers";
import Conquistas from "../painel/Conquistas";

const API_BASE = process.env.REACT_APP_API_BASE;

/**
 * Anima um número do valor atual até o valor final.
 */
const useContagem = (valorFinal, duracaoMs = 900) => {
  const [valorAtual, setValorAtual] = useState(0);
  const valorAnteriorRef = useRef(0);
  const frameRef = useRef(null);

  useEffect(() => {
    const destino =
      typeof valorFinal === "number" && Number.isFinite(valorFinal)
        ? valorFinal
        : 0;

    const origem = valorAnteriorRef.current;
    const inicio = performance.now();

    const animar = (timestamp) => {
      const decorrido = timestamp - inicio;
      const progresso = Math.min(decorrido / duracaoMs, 1);

      const progressoSuave = 1 - Math.pow(1 - progresso, 3);
      const valorCalculado =
        origem + (destino - origem) * progressoSuave;

      setValorAtual(Math.round(valorCalculado));

      if (progresso < 1) {
        frameRef.current = requestAnimationFrame(animar);
      } else {
        valorAnteriorRef.current = destino;
      }
    };

    frameRef.current = requestAnimationFrame(animar);

    return () => {
      if (frameRef.current) {
        cancelAnimationFrame(frameRef.current);
      }
    };
  }, [valorFinal, duracaoMs]);

  return valorAtual;
};

const PaginaNivel = () => {
  const [xpTotal, setXpTotal] = useState(0);
  const [nivel, setNivel] = useState(1);
  const [resumo, setResumo] = useState(null);
  const [conquistas, setConquistas] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState("");

  useEffect(() => {
    let componenteAtivo = true;

    const buscarDados = async () => {
      setCarregando(true);
      setErro("");

      try {
        const [resNivel, resConquistas] = await Promise.all([
          fetch(`${API_BASE}/nivel-dados.php`, {
            credentials: "include",
          }),
          fetch(`${API_BASE}/conquistas-listar.php`, {
            credentials: "include",
          }),
        ]);

        if (!resNivel.ok || !resConquistas.ok) {
          throw new Error("Não foi possível carregar os dados.");
        }

        const [dataNivel, dataConquistas] = await Promise.all([
          resNivel.json(),
          resConquistas.json(),
        ]);

        if (!componenteAtivo) return;

        if (dataNivel.status === "sucesso") {
          setXpTotal(Number(dataNivel.xp_total) || 0);
          setNivel(Math.max(1, Number(dataNivel.nivel) || 1));
          setResumo(dataNivel.resumo_fontes || null);
        } else {
          throw new Error(
            dataNivel.mensagem || "Erro ao carregar os dados do nível."
          );
        }

        if (dataConquistas.status === "sucesso") {
          setConquistas(
            Array.isArray(dataConquistas.conquistas)
              ? dataConquistas.conquistas
              : []
          );
        }
      } catch (error) {
        if (!componenteAtivo) return;

        console.error("Erro ao carregar a página de nível:", error);
        setErro(
          "Não foi possível carregar seu progresso agora. Tente novamente."
        );
      } finally {
        if (componenteAtivo) {
          setCarregando(false);
        }
      }
    };

    buscarDados();

    return () => {
      componenteAtivo = false;
    };
  }, []);

  const tier = useMemo(() => getNivelTier(nivel), [nivel]);

  const progresso = useMemo(() => {
    const xpInicioNivel = 50 * nivel * (nivel - 1);
    const xpProximoNivel = 50 * (nivel + 1) * nivel;

    const xpNecessarioNivel = Math.max(
      1,
      xpProximoNivel - xpInicioNivel
    );

    const xpProgressoNivel = Math.max(
      0,
      Math.min(xpTotal - xpInicioNivel, xpNecessarioNivel)
    );

    const xpRestante = Math.max(
      0,
      xpNecessarioNivel - xpProgressoNivel
    );

    const percentual = Math.max(
      0,
      Math.min(
        100,
        Math.round(
          (xpProgressoNivel / xpNecessarioNivel) * 100
        )
      )
    );

    return {
      xpInicioNivel,
      xpProximoNivel,
      xpNecessarioNivel,
      xpProgressoNivel,
      xpRestante,
      percentual,
    };
  }, [nivel, xpTotal]);

  const xpTotalAnimado = useContagem(
    carregando ? 0 : xpTotal,
    1000
  );

  const refeicoesAnimado = useContagem(
    carregando ? 0 : Number(resumo?.refeicoes) || 0,
    800
  );

  const conquistasAnimado = useContagem(
    carregando ? 0 : Number(resumo?.conquistas) || 0,
    800
  );

  const diasAguaAnimado = useContagem(
    carregando ? 0 : Number(resumo?.dias_agua) || 0,
    800
  );

  if (carregando) {
    return (
      <div className="pagina-nivel-estado">
        <div className="pagina-nivel-loader">
          <div className="pagina-nivel-loader-anel" />
          <div className="pagina-nivel-loader-icone">
            <FontAwesomeIcon icon={faTrophy} />
          </div>
        </div>

        <div className="pagina-nivel-carregando-titulo">
          Carregando seu progresso
        </div>

        <div className="pagina-nivel-carregando-texto">
          Organizando níveis, XP e conquistas...
        </div>
      </div>
    );
  }

  if (erro) {
    return (
      <div className="pagina-nivel-estado">
        <div className="pagina-nivel-erro-icone">
          <FontAwesomeIcon icon={faTrophy} />
        </div>

        <div className="pagina-nivel-carregando-titulo">
          Não foi possível carregar
        </div>

        <div className="pagina-nivel-carregando-texto">
          {erro}
        </div>

        <button
          type="button"
          className="pagina-nivel-tentar-novamente"
          onClick={() => window.location.reload()}
        >
          Tentar novamente
        </button>
      </div>
    );
  }

  return (
    <main
      className="pagina-nivel-pagina"
      style={{
        "--tier-cor": tier.corPrincipal,
        "--tier-brilho": tier.corBrilho,
      }}
    >
      <div className="pagina-nivel-container">
        <header className="pagina-nivel-topo">
          <button
            type="button"
            className="pagina-nivel-voltar"
            onClick={() => window.history.back()}
            aria-label="Voltar para a tela anterior"
          >
            <FontAwesomeIcon icon={faArrowLeft} />
          </button>

          <div className="pagina-nivel-topo-centro">
            <img
  src="/assets/logo2.png"
  alt="NutriFácil"
  className="painel-logo"
/>


            <h1 className="pagina-nivel-topo-titulo">
              Meu progresso
            </h1>
          </div>

          <div
            className="pagina-nivel-topo-espaco"
            aria-hidden="true"
          />


          
        </header>

        <section
          className={
            "pagina-nivel-hero" +
            (tier.brilhoIntenso
              ? " pagina-nivel-hero-brilho-intenso"
              : "")
          }
        >
          <div className="pagina-nivel-hero-fundo" />
          <div className="pagina-nivel-hero-luz pagina-nivel-hero-luz-um" />
          <div className="pagina-nivel-hero-luz pagina-nivel-hero-luz-dois" />
          <div className="pagina-nivel-hero-grade" />

          <div className="pagina-nivel-hero-conteudo">
            <div className="pagina-nivel-emblema-area">
              <div className="pagina-nivel-emblema-halo" />

              <div className="pagina-nivel-emblema-circulo">
                <div className="pagina-nivel-emblema-grande">
                  {tier.icone}
                </div>
              </div>

              <div className="pagina-nivel-emblema-nivel">
                {nivel}
              </div>
            </div>

            <div className="pagina-nivel-hero-textos">
              <span className="pagina-nivel-tier-badge">
                <FontAwesomeIcon icon={faMedal} />
                {tier.nome}
              </span>

              <h2 className="pagina-nivel-numero">
                Nível {nivel}
              </h2>

              <p className="pagina-nivel-mensagem">
                Continue evoluindo e desbloqueie novas conquistas.
              </p>
            </div>

            <div className="pagina-nivel-xp-bloco">
              <div className="pagina-nivel-xp-cabecalho">
                <div>
                  <span className="pagina-nivel-xp-sobretitulo">
                    Experiência total
                  </span>

                  <div className="pagina-nivel-xp-total">
                    {xpTotalAnimado.toLocaleString("pt-BR")}
                    <span> XP</span>
                  </div>
                </div>

                <div className="pagina-nivel-percentual">
                  {progresso.percentual}%
                </div>
              </div>

              <div
                className="pagina-nivel-barra-fundo"
                role="progressbar"
                aria-valuemin="0"
                aria-valuemax="100"
                aria-valuenow={progresso.percentual}
                aria-label={`Progresso para o nível ${nivel + 1}`}
              >
                <div
                  className="pagina-nivel-barra-preenchida"
                  style={{
                    width: `${progresso.percentual}%`,
                  }}
                >
                  <div className="pagina-nivel-barra-reflexo" />
                  <div className="pagina-nivel-barra-ponta" />
                </div>
              </div>

              <div className="pagina-nivel-progresso-rodape">
                <span>
                  {progresso.xpProgressoNivel.toLocaleString(
                    "pt-BR"
                  )}{" "}
                  de{" "}
                  {progresso.xpNecessarioNivel.toLocaleString(
                    "pt-BR"
                  )}{" "}
                  XP
                </span>

                <span>
                  Faltam{" "}
                  <strong>
                    {progresso.xpRestante.toLocaleString(
                      "pt-BR"
                    )}{" "}
                    XP
                  </strong>
                </span>
              </div>

              <div className="pagina-nivel-proximo-nivel">
                <span>
                  Próxima meta
                </span>

                <strong>
                  Nível {nivel + 1}
                </strong>

                <FontAwesomeIcon icon={faChevronRight} />
              </div>
            </div>
          </div>
        </section>

        {resumo && (
          <section className="pagina-nivel-resumo-secao">
            <div className="pagina-nivel-secao-cabecalho">
              <div>
                <span className="pagina-nivel-secao-sobretitulo">
                  Visão geral
                </span>

                <h2 className="pagina-nivel-secao-titulo">
                  Sua jornada
                </h2>
              </div>

              <div className="pagina-nivel-secao-icone">
                <FontAwesomeIcon icon={faBolt} />
              </div>
            </div>

            <div className="pagina-nivel-resumo-grid">
              <article className="pagina-nivel-resumo-card pagina-nivel-card-refeicao">
                <div className="pagina-nivel-resumo-icone-area">
                  <FontAwesomeIcon
                    icon={faUtensils}
                    className="pagina-nivel-resumo-icone"
                  />
                </div>

                <div className="pagina-nivel-resumo-conteudo">
                  <div className="pagina-nivel-resumo-numero">
                    {refeicoesAnimado.toLocaleString("pt-BR")}
                  </div>

                  <div className="pagina-nivel-resumo-label">
                    Refeições
                  </div>

                  <div className="pagina-nivel-resumo-descricao">
                    cadastradas no app
                  </div>
                </div>
              </article>

              <article className="pagina-nivel-resumo-card pagina-nivel-card-conquista">
                <div className="pagina-nivel-resumo-icone-area">
                  <FontAwesomeIcon
                    icon={faTrophy}
                    className="pagina-nivel-resumo-icone"
                  />
                </div>

                <div className="pagina-nivel-resumo-conteudo">
                  <div className="pagina-nivel-resumo-numero">
  {conquistasAnimado.toLocaleString("pt-BR")} / {conquistas.length}
</div>

                  <div className="pagina-nivel-resumo-label">
                    Conquistas
                  </div>

                  <div className="pagina-nivel-resumo-descricao">
                    desbloqueadas
                  </div>
                </div>
              </article>

              {resumo.dias_agua !== null &&
                resumo.dias_agua !== undefined && (
                  <article className="pagina-nivel-resumo-card pagina-nivel-card-agua">
                    <div className="pagina-nivel-resumo-icone-area">
                      <FontAwesomeIcon
                        icon={faDroplet}
                        className="pagina-nivel-resumo-icone"
                      />
                    </div>

                    <div className="pagina-nivel-resumo-conteudo">
                      <div className="pagina-nivel-resumo-numero">
                        {diasAguaAnimado.toLocaleString("pt-BR")}
                      </div>

                      <div className="pagina-nivel-resumo-label">
                        Metas de água
                      </div>

                      <div className="pagina-nivel-resumo-descricao">
                        concluídas
                      </div>
                    </div>
                  </article>
                )}
            </div>
          </section>
        )}

        <section className="pagina-nivel-conquistas-secao">
          <div className="pagina-nivel-secao-cabecalho pagina-nivel-conquistas-cabecalho">
            <div>
              <span className="pagina-nivel-secao-sobretitulo">
                Coleção
              </span>

              <h2 className="pagina-nivel-secao-titulo">
                Conquistas
              </h2>

              <p className="pagina-nivel-secao-descricao">
                Complete desafios e aumente sua coleção de medalhas.
              </p>
            </div>

            <div className="pagina-nivel-secao-icone pagina-nivel-secao-icone-trofeu">
              <FontAwesomeIcon icon={faTrophy} />
            </div>
          </div>

          <div className="pagina-nivel-conquistas-conteudo">
            <Conquistas conquistas={conquistas} />
          </div>
        </section>
      </div>
    </main>
  );
};

export default PaginaNivel;